"use client";
import React, { useState } from "react";
import { Mail, ArrowRight } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "react-toastify";
import { apiVerifyCaptcha } from "@/apis/captcha";
import { apiSubscribeNewsletter } from "@/apis/page_optimization";

interface SubscribeFormProps {
  domain: string;
  companyId: string;
}

const SubscribeForm = ({ domain, companyId }: SubscribeFormProps) => {
  const [email, setEmail] = useState<string>("");
  const [status, setStatus] = useState<'idle' | 'verifying' | 'sending' | 'failed'>('idle');

  const buttonLabel = {
    idle: 'Subscribe Now',
    verifying: 'Verifying...',
    sending: 'Subscribing...',
    failed: 'Failed. Try Again',
  }[status];

  const { executeRecaptcha } = useGoogleReCaptcha();

  const validateEmail = (emailStr: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailStr);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Email field is required.', { autoClose: 2000 });
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.", { autoClose: 2000 });
      return;
    }

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not ready. Please try again.', { autoClose: 2000 });
      return;
    }

    try {
      // Step 1: Verify token using domain
      setStatus('verifying');
      const recaptchaToken = await executeRecaptcha('newsletter_form');
      const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);

      if (!captchaRes?.verified) {
        setStatus('failed');
        toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
        return;
      }

      // Step 2: Subscribe newsletter using companyId
      setStatus('sending');
      const response = await apiSubscribeNewsletter(companyId, email);

      if (response?.status === 200 || response?.message === "Subscribed successfully") {
        toast.success("Thank you for your feedback!", { autoClose: 2000 });
        setEmail("");
        setStatus('idle');
      } else {
        toast.error("You have already subscribed to our newsletter.");
        setStatus('idle');
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error('Failed to subscribe. Please try again later.', { autoClose: 2000 });
      setStatus('failed');
    } finally {
      if (status !== 'idle' && status !== 'failed') {
        setStatus('idle');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative flex flex-col gap-3.5 w-full max-w-sm group">
      <div className="relative">
        <Mail
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#800000] transition-colors"
          size={18}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'verifying' || status === 'sending'}
          placeholder="Enter your email address"
          className="w-full h-[52px] bg-[#222222] border border-[#333333] focus:border-[#800000]/60 text-white pl-11 pr-4 rounded-xl outline-none transition-all text-sm tracking-wide placeholder:text-slate-600 disabled:opacity-50"
        />
      </div>

      <button
        type="submit"
        disabled={status === 'verifying' || status === 'sending'}
        className="w-full h-[52px] rounded-xl bg-[#800000] text-white font-black uppercase tracking-widest hover:bg-[#800000]/80 active:scale-[0.99] transition-all flex items-center justify-center gap-2.5 text-xs shadow-lg shadow-[#FF5A00]/10 disabled:bg-[#800000]/50 disabled:cursor-not-allowed"
      >
        {buttonLabel}
        {status === 'idle' && (
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-1"
          />
        )}
      </button>

      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest text-center mt-1">
        No spam. Just pure savings.
      </p>
    </form>
  );
};

export default SubscribeForm;